import { Publish, Warning } from '@mui/icons-material';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    ListItemText,
    Menu,
    MenuItem,
    Stack,
    TextField,
    Tooltip,
    Typography
} from '@mui/material';
import { red } from '@mui/material/colors';
import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { inject, observer } from 'mobx-react';
import moment from 'moment';

import SituacaoPublicacaoPauta, { SITUACAO_PUBLICACAO_PAUTA } from '@/dominio/SituacaoPublicacaoPauta';

import ReactComponent from './ReactComponent';

class IconeSituacaoPublicacao extends ReactComponent {
    anchorElCancelarDisponibilizacaoDJEN = null;
    anchorElVisualizarDisponibilizacaoDJEN = null;
    openDialogCancelarDisponibilizacaoDJEN = false;

    motivoCancelamento = '';
    erroMotivoCancelamento = '';

    constructor(props) {
        super(props);

        makeObservable(this, {
            visualizarPublicacao: true,
            render: true,
            renderMenuVisualizar: true,
            renderMenuCancelar: true,
            renderDialogCancelar: true,
            anchorElCancelarDisponibilizacaoDJEN: observable,
            anchorElVisualizarDisponibilizacaoDJEN: observable,
            openDialogCancelarDisponibilizacaoDJEN: observable,
            motivoCancelamento: observable,
            erroMotivoCancelamento: observable,
            descricao: computed,
            openMenuCancelarDisponibilizacaoDJEN: computed,
            openMenuVisualizarDisponibilizacaoDJEN: computed,
            situacao: computed,
            cor: computed,
            titulo: computed,
            canClick: computed,
            handleClick: action,
            handleCloseMenuCancelar: action,
            handleCloseMenuVisualizar: action,
            handleOpenDialogCancelar: action,
            handleCloseDialogCancelar: action,
            cancelarDisponibilizacaoDJEN: action,
            handleAlteraMotivoCancelamento: action
        });
    }

    get descricao() {
        const desc =
            this.observableProps.registro.materiaPublicacaoPauta?.tipoPublicacao === 'EDITAL_CANCELAMENTO'
                ? 'EDITAL DE CANCELAMENTO  - '
                : '';

        if (this.observableProps.registro.materiaPublicacaoPauta?.situacao) {
            return (
                desc +
                SituacaoPublicacaoPauta.getDescricao(this.observableProps.registro.materiaPublicacaoPauta.situacao)
            );
        }

        if (this.observableProps.registro.confirmadaPublicacaoPauta) {
            return desc + SITUACAO_PUBLICACAO_PAUTA.LIBERADO_PARA_DISPONIBILIZACAO.descricao;
        }

        return desc + 'Situação de publicação de pauta DESCONHECIDA';
    }

    get openMenuCancelarDisponibilizacaoDJEN() {
        return this.anchorElCancelarDisponibilizacaoDJEN;
    }

    get openMenuVisualizarDisponibilizacaoDJEN() {
        return this.anchorElVisualizarDisponibilizacaoDJEN;
    }

    get situacao() {
        if (this.observableProps.registro.materiaPublicacaoPauta?.situacao) {
            return this.observableProps.registro.materiaPublicacaoPauta.situacao;
        }
        if (this.observableProps.registro.confirmadaPublicacaoPauta) {
            return SITUACAO_PUBLICACAO_PAUTA.LIBERADO_PARA_DISPONIBILIZACAO.id;
        }
        return null;
    }

    get cor() {
        if (this.observableProps.registro.materiaPublicacaoPauta?.tipoPublicacao === 'EDITAL_CANCELAMENTO') {
            return SituacaoPublicacaoPauta.getCorEditalCancelamento(
                this.observableProps.registro.materiaPublicacaoPauta.situacao
            );
        }
        if (this.observableProps.registro.materiaPublicacaoPauta?.situacao) {
            return SituacaoPublicacaoPauta.getCor(this.observableProps.registro.materiaPublicacaoPauta.situacao);
        }

        if (this.observableProps.registro.confirmadaPublicacaoPauta) {
            return SITUACAO_PUBLICACAO_PAUTA.LIBERADO_PARA_DISPONIBILIZACAO.cor;
        }

        return 'black';
    }

    // eslint-disable-next-line sonarjs/cognitive-complexity
    get titulo() {
        let title = this.descricao;

        if (this.observableProps.registro.materiaPublicacaoPauta) {
            switch (this.observableProps.registro.materiaPublicacaoPauta.situacao) {
                case SITUACAO_PUBLICACAO_PAUTA.AGUARDANDO_PROCESSAMENTO.id:
                case SITUACAO_PUBLICACAO_PAUTA.EM_PROCESSAMENTO.id:
                case SITUACAO_PUBLICACAO_PAUTA.LIBERADO_PARA_DISPONIBILIZACAO.id:
                    title +=
                        this.observableProps.registro.materiaPublicacaoPauta.dataEnvio ||
                        this.observableProps.registro.materiaPublicacaoPauta.dataPrevistaDisponibilizacao
                            ? ' ('
                            : '';
                    title += this.observableProps.registro.materiaPublicacaoPauta.dataEnvio
                        ? `enviado em ${moment(
                              this.observableProps.registro.materiaPublicacaoPauta.dataEnvio,
                              'DD-MM-YYYY-HH-mm-ss'
                          ).format('DD/MM/YYYY HH:mm')}`
                        : ' (data de envio desconhecida';
                    title += this.observableProps.registro.materiaPublicacaoPauta.dataPrevistaDisponibilizacao
                        ? `, com disponibilização prevista para ${moment(
                              this.observableProps.registro.materiaPublicacaoPauta.dataPrevistaDisponibilizacao,
                              'DD-MM-YYYY'
                          ).format('DD/MM/YYYY')}`
                        : '; data de previsão de disponibilização desconhecida';
                    title +=
                        this.observableProps.registro.materiaPublicacaoPauta.dataEnvio ||
                        this.observableProps.registro.materiaPublicacaoPauta.dataPrevistaDisponibilizacao
                            ? ')'
                            : '';
                    break;
                case SITUACAO_PUBLICACAO_PAUTA.DISPONIBILIZADO.id:
                case SITUACAO_PUBLICACAO_PAUTA.DISPONIBILIZADO_PARCIALMENTE.id:
                    title += this.observableProps.registro.materiaPublicacaoPauta.dataRealDisponibilizacao
                        ? ` em ${moment(
                              this.observableProps.registro.materiaPublicacaoPauta.dataRealDisponibilizacao,
                              'DD-MM-YYYY'
                          ).format('DD/MM/YYYY')}`
                        : '; data de disponibilização desconhecida';
                    title +=
                        this.observableProps.registro.materiaPublicacaoPauta.dataEnvio ||
                        this.observableProps.registro.materiaPublicacaoPauta.dataRealDisponibilizacao
                            ? ' ('
                            : '';
                    title += this.observableProps.registro.materiaPublicacaoPauta.dataEnvio
                        ? `enviado em ${moment(
                              this.observableProps.registro.materiaPublicacaoPauta.dataEnvio,
                              'DD-MM-YYYY-HH-mm-ss'
                          ).format('DD/MM/YYYY HH:mm')}`
                        : ' (data de envio desconhecida';
                    title += this.observableProps.registro.materiaPublicacaoPauta.dataRealDisponibilizacao
                        ? ` e publicado em ${this.dominioStore.getProximaDataUtilPublicacao(
                              this.observableProps.registro.materiaPublicacaoPauta.dataRealDisponibilizacao,
                              'DD-MM-YYYY',
                              'DD/MM/YYYY'
                          )}`
                        : '; data de publicação desconhecida';
                    title +=
                        this.observableProps.registro.materiaPublicacaoPauta.dataEnvio ||
                        this.observableProps.registro.materiaPublicacaoPauta.dataRealDisponibilizacao
                            ? ')'
                            : '';
                    break;
                default:
                    break;
            }
        }

        return title;
    }

    get canClick() {
        return this.situacao !== SITUACAO_PUBLICACAO_PAUTA.CANCELADO.id;
    }

    // eslint-disable-next-line unicorn/consistent-class-member-order
    handleClick = (event) => {
        if (this.situacao === SITUACAO_PUBLICACAO_PAUTA.DISPONIBILIZADO.id) {
            this.anchorElVisualizarDisponibilizacaoDJEN = event.currentTarget;
        } else if (this.situacao !== SITUACAO_PUBLICACAO_PAUTA.CANCELADO.id) {
            this.anchorElCancelarDisponibilizacaoDJEN = event.currentTarget;
        }
    };

    visualizarPublicacao = () => {
        if (this.observableProps.registro.materiaPublicacaoPauta?.linkVisualizacaoPublicacao) {
            window.open(this.observableProps.registro.materiaPublicacaoPauta.linkVisualizacaoPublicacao);
        }
    };

    handleCloseMenuCancelar = () => {
        this.anchorElCancelarDisponibilizacaoDJEN = null;
    };

    handleCloseMenuVisualizar = () => {
        this.anchorElVisualizarDisponibilizacaoDJEN = null;
    };

    handleOpenDialogCancelar = () => {
        this.openDialogCancelarDisponibilizacaoDJEN = true;
    };

    handleCloseDialogCancelar = () => {
        this.openDialogCancelarDisponibilizacaoDJEN = false;
        this.handleCloseMenuCancelar();
    };

    cancelarDisponibilizacaoDJEN = async () => {
        if (this.motivoCancelamento === '') {
            this.erroMotivoCancelamento = 'Informe o motivo do cancelamento.';
        } else {
            try {
                const resultado = await this.sessaoService.cancelarDisponibilizacaoDiario(
                    this.observableProps.registro.sessaoId,
                    this.observableProps.registro.processo.numeroCompleto,
                    this.motivoCancelamento
                );

                if (resultado.data && resultado.data.tipo === 'SUCESSO') {
                    this.mensagemStore.abrirMensagemSucesso(
                        'O cancelamento da disponibilização no DJEN foi executado com sucesso.'
                    );
                    this.sessaoStore.recarregarProcesso(this.observableProps.registro, true);
                } else {
                    const mensagem = resultado?.data?.mensagem
                        ? resultado?.data?.mensagem
                        : 'Erro ao cancelar disponibilização.';
                    this.mensagemStore.abrirMensagemErro(mensagem);
                }
            } catch (err) {
                // @ts-ignore
                if (err.response.status === 400 && err.response.data?.mensagem) {
                    // @ts-ignore
                    this.mensagemStore.abrirMensagemAlerta(err.response.data.mensagem);
                    // @ts-ignore
                } else if (err.response.status === 404 && err.response.data?.mensagem) {
                    // @ts-ignore
                    this.mensagemStore.abrirMensagemErro(err.response.data.mensagem);
                } else {
                    this.mensagemStore.abrirMensagemErro('Falha ao tentar cancelar disponibilização no DJEN.');
                }
            } finally {
                this.handleCloseDialogCancelar();
                runInAction(() => {
                    this.motivoCancelamento = '';
                });
            }
        }
    };

    handleAlteraMotivoCancelamento = (e) => {
        const motivoCancelamento = e.target.value;
        this.motivoCancelamento = motivoCancelamento;

        if (motivoCancelamento !== '') {
            this.erroMotivoCancelamento = '';
        }
    };

    render() {
        return (
            this.situacao && (
                <Box sx={{ display: 'flex' }}>
                    <Tooltip
                        onClick={this.handleClick}
                        title={<Typography>{this.titulo}</Typography>}
                    >
                        <Publish
                            sx={{ color: this.cor, fillOpacity: 0.7, cursor: this.canClick ? 'pointer' : 'default' }}
                        />
                    </Tooltip>
                    {this.renderMenuVisualizar()}
                    {this.renderMenuCancelar()}
                    {this.renderDialogCancelar()}
                </Box>
            )
        );
    }

    renderMenuVisualizar() {
        return (
            this.openMenuVisualizarDisponibilizacaoDJEN && (
                <Menu
                    anchorEl={this.anchorElVisualizarDisponibilizacaoDJEN}
                    anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
                    id="visualizar-publicacao-djen"
                    onClose={this.handleCloseMenuVisualizar}
                    open={this.openMenuVisualizarDisponibilizacaoDJEN}
                    transformOrigin={{ horizontal: 'left', vertical: 'top' }}
                >
                    <MenuItem onClick={this.visualizarPublicacao}>
                        <ListItemText>
                            <Typography fontSize="small">Visualizar publicação no DJEN</Typography>
                        </ListItemText>
                    </MenuItem>
                </Menu>
            )
        );
    }

    renderMenuCancelar() {
        return (
            this.openMenuCancelarDisponibilizacaoDJEN && (
                <Menu
                    anchorEl={this.anchorElCancelarDisponibilizacaoDJEN}
                    anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
                    id="cancela-disponibilizacao-djen"
                    onClose={this.handleCloseMenuCancelar}
                    open={this.openMenuCancelarDisponibilizacaoDJEN}
                    transformOrigin={{ horizontal: 'left', vertical: 'top' }}
                >
                    <MenuItem onClick={this.handleOpenDialogCancelar}>
                        <ListItemText>
                            <Typography fontSize="small">Cancelar disponibilização no DJEN</Typography>
                        </ListItemText>
                    </MenuItem>
                </Menu>
            )
        );
    }

    renderDialogCancelar() {
        if (!this.openMenuCancelarDisponibilizacaoDJEN) {
            return null;
        }

        return (
            <>
                <Dialog
                    aria-describedby="alert-dialog-description"
                    aria-labelledby="alert-dialog-title"
                    onClose={this.handleCloseDialogCancelar}
                    open={this.openDialogCancelarDisponibilizacaoDJEN}
                >
                    <DialogTitle id="alert-dialog-title" />

                    <DialogActions>
                        <Button onClick={this.handleCloseDialogCancelar}>Cancelar</Button>
                        <Button onClick={this.cancelarDisponibilizacaoDJEN}>Confirmar</Button>
                    </DialogActions>
                </Dialog>
                <Dialog
                    aria-describedby="alert-dialog-description"
                    aria-labelledby="alert-dialog-title"
                    onClose={this.handleCloseDialogCancelar}
                    open={!!this.openDialogCancelarDisponibilizacaoDJEN}
                >
                    <DialogTitle id="alert-dialog-title">Cancelar disponibilização no DJEN</DialogTitle>
                    <DialogContent>
                        <Stack
                            direction="row"
                            spacing={2}
                        >
                            <Box>
                                <Warning sx={{ verticalAlign: 'middle', color: 'orange' }} />
                            </Box>
                            <Box>
                                <Typography align={'justify'}>
                                    <b>ATENÇÃO</b>: O cancelamento da disponibilização do envio ao DJEN não remove as
                                    movimentações relacionadas à inclusão do processo em pauta, caso seja necessário
                                    corrija ou exclua os registros manualmente no sistema de origem.
                                </Typography>
                            </Box>
                        </Stack>
                        <Box sx={{ mt: 1 }}>
                            <TextField
                                fullWidth
                                multiline
                                slotProps={{
                                    inputLabel: {
                                        shrink: true
                                    }
                                }}
                                error={this.erroMotivoCancelamento !== ''}
                                label="Motivo do cancelamento"
                                minRows={4}
                                onChange={this.handleAlteraMotivoCancelamento}
                                value={this.motivoCancelamento}
                                variant="filled"
                            />
                            <Typography
                                color={red[500]}
                                sx={{ mt: 1 }}
                            >
                                {this.erroMotivoCancelamento}
                            </Typography>
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button
                            color="secondary"
                            onClick={this.handleCloseDialogCancelar}
                            variant="outlined"
                        >
                            Cancelar
                        </Button>
                        <Button
                            onClick={this.cancelarDisponibilizacaoDJEN}
                            variant="outlined"
                        >
                            Confirmar
                        </Button>
                    </DialogActions>
                </Dialog>
            </>
        );
    }
}

export default inject('stores')(observer(IconeSituacaoPublicacao));
